/**
 * useLyricsAutoFetch
 *
 * Automatically fetches time-synced LRC lyrics from LRCLIB when an MP3
 * filename is provided. Handles ambiguous, common-word song titles like
 * "Hello" by using a relevance scoring system and a two-pass search strategy.
 *
 * ─── SEARCH STRATEGY ──────────────────────────────────────────────────────
 *
 * Pass 1 — Flat query search
 *   GET /api/search?q=hello+adele
 *   Results are scored by title/artist relevance (see scoreTrack below).
 *
 * Pass 2 — Explicit title + artist search (when Pass 1 score is low)
 *   GET /api/search?track_name=hello&artist_name=adele
 *   LRCLIB's structured search is far more accurate for common-word titles.
 *   Only triggered when Pass 1 best score < CONFIDENCE_THRESHOLD.
 *
 * Confidence levels:
 *   HIGH   (score ≥ 60): Inject lyrics automatically. "hello adele.mp3"
 *                         cleanly maps to "Hello — Adele" with all words
 *                         matching both name and artist → score 80+.
 *   LOW    (score < 60): Set status "confirm" — show the talent the matched
 *                         song and ask them to accept or reject. Prevents
 *                         silently injecting "Hello" by Lionel Richie when
 *                         the talent uploaded an Adele track.
 *   NONE   (no results): Set status "not_found" — manual upload fallback.
 *
 * ─── FILENAME CLEANING ────────────────────────────────────────────────────
 *
 * "01 - Hello (Official Music Video) [HD].mp3"
 *   → strip extension        → "01 - Hello (Official Music Video) [HD]"
 *   → strip track numbers    → "Hello (Official Music Video) [HD]"
 *   → strip square brackets  → "Hello (Official Music Video)"
 *   → strip common parens    → "Hello"
 *   → normalise separators   → "Hello"                 ← title only
 *
 * "hello - adele.mp3" or "adele - hello.mp3"
 *   → cleaned                → "hello adele"           ← title + artist
 *   → splitTitleArtist()     → { title: "hello", artist: "adele" }
 *   → Pass 2 with both       → exact LRCLIB match
 *
 * ─── STATUS VALUES ────────────────────────────────────────────────────────
 *
 *   "idle"      — no filename provided
 *   "searching" — LRCLIB request(s) in flight
 *   "found"     — synced LRC auto-injected (high confidence)
 *   "plain"     — plain lyrics only, fake timestamps applied (high confidence)
 *   "confirm"   — low confidence match, needs talent confirmation
 *   "not_found" — no results in either pass
 *   "error"     — network failure
 */

import { useCallback, useRef, useState } from "react";

/* ── Types ─────────────────────────────────────────────────────────────── */

export type LyricsStatus =
  | "idle"
  | "searching"
  | "found"
  | "plain"
  | "confirm"
  | "not_found"
  | "error";

export interface LyricsResult {
  status:     LyricsStatus;
  lrc:        string;     // LRC string ready to pass to handleLyricsChange
  title:      string;     // matched song title from LRCLIB
  artist:     string;     // matched artist
  duration:   number;     // matched duration in seconds
  query:      string;     // the cleaned query that was searched
  confidence: number;     // 0–100 relevance score of the best match
}

interface LrcLibTrack {
  id:           number;
  name:         string;
  artistName:   string;
  albumName:    string;
  duration:     number;
  syncedLyrics: string | null;
  plainLyrics:  string | null;
}

/* ── Constants ──────────────────────────────────────────────────────────── */

// Below this score, result status is "confirm" instead of auto-injecting.
// Calibrated so that "hello adele" → score ~80 (auto), "hello" alone → score ~15 (confirm).
const CONFIDENCE_THRESHOLD = 55;

const LRCLIB_BASE = "https://lrclib.net/api";

/* ── Filename cleaning ──────────────────────────────────────────────────── */

/**
 * Recommended MR file naming: "Song Title - Artist Name.mp3"
 *
 * The cleaner handles these formats automatically:
 *   "Hello - Adele.mp3"                    → "hello adele"
 *   "01 - Bohemian Rhapsody - Queen.mp3"   → "bohemian rhapsody queen"
 *   "Shape of You - Ed Sheeran [MR].mp3"   → "shape of you ed sheeran"
 *   "Hello - Adele (Official) 128kbps.mp3" → "hello adele"
 *   "Rolling in the Deep_Adele.mp3"        → "rolling in the deep adele"
 *
 * Single-word filenames ("hello.mp3") have no artist info — the hook will
 * fall through to the "confirm" state and ask the talent to verify the match.
 */
export function cleanFilenameToQuery(filename: string): string {
  return filename
    .replace(/\.[^.]+$/, "")                                              // strip extension
    .replace(/_/g, " ")                                                   // underscores → spaces
    .replace(/^(?:track\s*)?\d{1,3}[\s.\-–—]+/i, "")                    // leading track number: "01 -"
    .replace(/\[.*?\]/g, "")                                              // [Official], [HD], [MR], [Karaoke]
    .replace(/\b(19|20)\d{2}\b/g, "")                                    // standalone years: 2015, 2024
    .replace(/\b\d{2,3}\s*kbps\b/gi, "")                                // bitrate tags: 128kbps, 320kbps
    .replace(/\b(mr|inst|instrumental|karaoke|ver(?:sion)?|edit|mix|remaster(?:ed)?)\b/gi, "") // MR/quality tags
    .replace(/\([^)]*(?:official|video|audio|lyrics?|remaster|feat|ft\.|music|live|acoustic|cover|radio)[^)]*\)/gi, "") // strip annotation parens (Official Music Video, Live at..., etc.)
    .replace(/\s*[-–—]\s*/g, " ")                                         // normalise separators → space
    .replace(/\(\s*\)/g, "")                                              // empty parens remaining
    .replace(/\s{2,}/g, " ")                                              // collapse whitespace
    .trim()
    .toLowerCase();
}

/**
 * Attempt to detect title and artist separately from a cleaned query.
 *
 * Handles:
 *   "hello adele"        → { title: "hello",       artist: "adele" }       (last word = artist)
 *   "rolling in deep adele" → tries all split points, picks highest-score
 *   "hello"              → { title: "hello",       artist: "" }
 *
 * We use a heuristic: try every possible split point and return the pair
 * that is most likely. For now we use a simple last-word heuristic which
 * covers the most common filename patterns: "title artist.mp3".
 * More complex splits are handled by Pass 2's explicit search.
 */
export function splitTitleArtist(query: string): { title: string; artist: string } {
  const words = query.trim().split(/\s+/);

  // Single word — can't split
  if (words.length === 1) return { title: query, artist: "" };

  // "title by artist" pattern
  const byIdx = words.indexOf("by");
  if (byIdx > 0 && byIdx < words.length - 1) {
    return {
      title:  words.slice(0, byIdx).join(" "),
      artist: words.slice(byIdx + 1).join(" "),
    };
  }

  // Common pattern: last 1–2 words are the artist
  // "hello adele" → title="hello", artist="adele"
  // "rolling in the deep adele" → title="rolling in the deep", artist="adele"
  // We try last-1 word as artist (most common for single-name artists like Adele, Rihanna)
  return {
    title:  words.slice(0, -1).join(" "),
    artist: words[words.length - 1],
  };
}

/* ── Relevance scoring ──────────────────────────────────────────────────── */

/**
 * Score a LRCLIB track against a cleaned query.
 *
 * Scoring rationale:
 *   100 — exact title match (query === track name)
 *    80 — all query words present in name+artist combined
 *    60 — all query words present in name only
 *    10 — per query word found in name
 *    10 — per query word found in artist name
 *     5 — bonus for longer (more complete) lyrics
 *    -20 — penalty if synced lyrics are absent (prefer synced over plain)
 *
 * This scoring ensures:
 *   "hello adele" → "Hello — Adele" scores ~90 (all words match both fields)
 *   "hello adele" → "Hello — Lionel Richie" scores ~10 (only 'hello' matches)
 *   "hello"       → "Hello — Adele" scores ~25 (partial match, below threshold)
 *   "hello"       → "Hello — Lionel Richie" scores ~25 (same, needs confirm)
 */
function scoreTrack(track: LrcLibTrack, query: string): number {
  const name     = track.name.toLowerCase().trim();
  const artist   = track.artistName.toLowerCase().trim();
  const combined = `${name} ${artist}`;
  const words    = query.toLowerCase().split(/\s+/).filter(Boolean);

  let score = 0;

  // Exact title match
  if (name === query.trim())                score += 100;

  // All words present in combined name+artist
  const allInCombined = words.every(w => combined.includes(w));
  if (allInCombined)                        score += 80;

  // All words present in title alone (artist not in filename)
  const allInName = words.every(w => name.includes(w));
  if (allInName && !allInCombined)          score += 60;

  // Per-word bonuses
  words.forEach(w => {
    if (name.includes(w))   score += 10;
    if (artist.includes(w)) score += 10;
  });

  // Lyrics completeness bonus (max +5)
  const lyricsLen = (track.syncedLyrics ?? track.plainLyrics ?? "").length;
  score += Math.min(lyricsLen / 500, 5);

  // Prefer synced over plain
  if (!track.syncedLyrics) score -= 20;

  return score;
}

/**
 * Score all tracks and return the best one, along with its score.
 * Only considers tracks that have some lyrics (synced or plain).
 */
function pickBestTrack(tracks: LrcLibTrack[], query: string): { track: LrcLibTrack; score: number } | null {
  const withLyrics = tracks.filter(t =>
    (t.syncedLyrics && t.syncedLyrics.trim().length > 20) ||
    (t.plainLyrics  && t.plainLyrics.trim().length  > 20)
  );
  if (!withLyrics.length) return null;

  const scored = withLyrics
    .map(t => ({ track: t, score: scoreTrack(t, query) }))
    .sort((a, b) => b.score - a.score);

  return scored[0];
}

/* ── LRC helpers ────────────────────────────────────────────────────────── */

/**
 * Convert plain lyrics to a basic LRC string with evenly-spaced fake timestamps.
 * At 4 seconds per line, a 3-minute song of ~45 lines works out perfectly.
 * KaraokeLyrics advances on each timestamp — so the display is still
 * sequential even without real timing data.
 */
function plainToLrc(plain: string, title: string, artist: string): string {
  const header   = `[ti:${title}]\n[ar:${artist}]\n`;
  const lines    = plain.split(/\r?\n/).filter(l => l.trim());
  const lrcLines = lines.map((l, i) => {
    const totalSecs = i * 4;
    const m = Math.floor(totalSecs / 60).toString().padStart(2, "0");
    const s = (totalSecs % 60).toString().padStart(2, "0");
    return `[${m}:${s}.00]${l}`;
  });
  return header + lrcLines.join("\n");
}

function trackToLrc(track: LrcLibTrack): string {
  if (track.syncedLyrics && track.syncedLyrics.trim().length > 20) {
    return track.syncedLyrics;
  }
  return plainToLrc(track.plainLyrics ?? "", track.name, track.artistName);
}

/* ── Backend API call ───────────────────────────────────────────────────────
 * Calls your NestJS POST /lyrics/search endpoint which chains:
 *   NetEase → LRCLIB → Musixmatch → Supabase cache
 *
 * BACKEND_URL is read from your environment:
 *   VITE_API_BASE_URL / REACT_APP_API_BASE_URL / NEXT_PUBLIC_API_BASE_URL
 *
 * The backend returns the same fields as the old LRCLIB response plus
 * a "source" field ("cache" | "netease" | "lrclib" | "musixmatch") and
 * a "confidence" score (0–100) that is already calculated server-side.
 *
 * If the backend is unreachable we fall back to calling LRCLIB directly
 * so development still works without the backend running locally.
 * ─────────────────────────────────────────────────────────────────────── */

const BACKEND_URL =
  (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_API_BASE_URL) ||
  (typeof process !== "undefined" && (process.env?.REACT_APP_API_BASE_URL ?? process.env?.NEXT_PUBLIC_API_BASE_URL)) ||
  "";

interface BackendLyricsResponse {
  status:     "found" | "plain" | "not_found" | "error";
  lrc:        string;
  title:      string;
  artist:     string;
  duration:   number;
  source:     "cache" | "netease" | "lrclib" | "musixmatch" | "manual";
  confidence: number;
}

async function searchViaBackend(
  query:  string,
  title:  string,
  artist: string,
): Promise<BackendLyricsResponse | null> {
  if (!BACKEND_URL) return null; // no backend configured — fall through to LRCLIB direct

  try {
    const res = await fetch(`${BACKEND_URL}/lyrics/search`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ query, title, artist }),
      signal:  AbortSignal.timeout(12000),
    });
    if (!res.ok) return null;
    return res.json() as Promise<BackendLyricsResponse>;
  } catch {
    return null; // backend unreachable — fall through to LRCLIB direct
  }
}

/* ── LRCLIB direct fallback (used when BACKEND_URL is not set) ──────────── */

async function flatSearch(query: string): Promise<LrcLibTrack[]> {
  const res = await fetch(
    `${LRCLIB_BASE}/search?q=${encodeURIComponent(query)}`,
    { signal: AbortSignal.timeout(8000) }
  );
  if (!res.ok) throw new Error(`LRCLIB flat search ${res.status}`);
  return res.json();
}

async function structuredSearch(title: string, artist: string): Promise<LrcLibTrack[]> {
  const params = new URLSearchParams({ track_name: title });
  if (artist) params.set("artist_name", artist);
  const res = await fetch(
    `${LRCLIB_BASE}/search?${params.toString()}`,
    { signal: AbortSignal.timeout(8000) }
  );
  if (!res.ok) throw new Error(`LRCLIB structured search ${res.status}`);
  return res.json();
}

/* ── Hook ─────────────────────────────────────────────────────────────────
 *
 * Usage in Sing4YouTalentPage:
 *
 *   const { result, fetchForFilename, confirmResult, reset } = useLyricsAutoFetch();
 *
 *   // On MP3 upload:
 *   fetchForFilename(file.name);
 *
 *   // Watch result — auto-inject when high confidence:
 *   useEffect(() => {
 *     if (result.status === "found" || result.status === "plain") {
 *       if (!lyricsLrc) handleLyricsChange(result.lrc);
 *     }
 *   }, [result.status]);
 *
 *   // When talent confirms a low-confidence match:
 *   confirmResult(); // promotes status "confirm" → "found"/"plain" and injects
 *
 * ─────────────────────────────────────────────────────────────────────── */

export function useLyricsAutoFetch() {
  const [result, setResult] = useState<LyricsResult>({
    status: "idle", lrc: "", title: "", artist: "", duration: 0, query: "", confidence: 0,
  });

  const abortRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setResult({ status: "idle", lrc: "", title: "", artist: "", duration: 0, query: "", confidence: 0 });
  }, []);

  /**
   * Called when the talent confirms a low-confidence "confirm" result.
   * Promotes the result to "found" or "plain" so the parent can inject it.
   */
  const confirmResult = useCallback(() => {
    setResult(prev => ({
      ...prev,
      status: prev.lrc.includes("[") ? "found" : "plain",
    }));
  }, []);

  /**
   * Search by explicit title + artist — used by cafe/audience pages when they
   * receive a karaoke_load RTM message with { title, artist } from the talent.
   * Skips the filename cleaning step since title+artist are already clean strings.
   * Falls back to title-only if artist is empty.
   */
  const fetchForTitleArtist = useCallback(async (title: string, artist: string) => {
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    const query = artist ? `${title} - ${artist}` : title;
    if (!query.trim()) return;

    setResult({ status: "searching", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });

    try {
      // Try backend first if configured
      const backendResult = await searchViaBackend(query, title, artist);
      if (abort.signal.aborted) return;

      if (backendResult && backendResult.status !== "not_found" && backendResult.status !== "error") {
        const score = backendResult.confidence;
        setResult({
          status:     score < CONFIDENCE_THRESHOLD ? "confirm" : backendResult.status,
          lrc:        backendResult.lrc,
          title:      backendResult.title,
          artist:     backendResult.artist,
          duration:   backendResult.duration,
          query,
          confidence: score,
        });
        return;
      }

      // Direct LRCLIB: structured search first (most accurate when we have both)
      // then flat as fallback
      let best: { track: LrcLibTrack; score: number } | null = null;
      let topScore = 0;

      if (artist) {
        // Pass 1: structured — most accurate when title+artist are both known
        try {
          const tracks1 = await structuredSearch(title, artist);
          if (!abort.signal.aborted) {
            const b = pickBestTrack(tracks1, query);
            if (b) { best = b; topScore = b.score; }
          }
        } catch {}
      }

      // Pass 2: flat query fallback
      if (!best || topScore < CONFIDENCE_THRESHOLD) {
        try {
          const tracks2 = await flatSearch(query);
          if (!abort.signal.aborted) {
            const b2 = pickBestTrack(tracks2, query);
            if (b2 && b2.score > topScore) { best = b2; topScore = b2.score; }
          }
        } catch {}
      }

      if (abort.signal.aborted) return;

      if (!best) {
        setResult({ status: "not_found", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });
        return;
      }

      const { track, score } = best;
      const hasSynced = !!(track.syncedLyrics && track.syncedLyrics.trim().length > 20);
      const lrc = trackToLrc(track);

      setResult({
        status:     score < CONFIDENCE_THRESHOLD ? "confirm" : (hasSynced ? "found" : "plain"),
        lrc,
        title:      track.name,
        artist:     track.artistName,
        duration:   track.duration,
        query,
        confidence: score,
      });

    } catch (err: any) {
      if (abort.signal.aborted) return;
      console.warn("[useLyricsAutoFetch fetchForTitleArtist]", err);
      setResult({ status: "error", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });
    }
  }, []);

  /**
   * Main search function — called immediately on MP3 upload.
   * Runs two passes: flat query first, structured title+artist second
   * if the first pass score is below the confidence threshold.
   */
  const fetchForFilename = useCallback(async (filename: string) => {
    abortRef.current?.abort();
    const abort = new AbortController();
    abortRef.current = abort;

    const query = cleanFilenameToQuery(filename);
    if (!query) {
      setResult({ status: "not_found", lrc: "", title: "", artist: "", duration: 0, query: "", confidence: 0 });
      return;
    }

    setResult({ status: "searching", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });

    try {
      const { title, artist } = splitTitleArtist(query);

      // ── Try backend first (NestJS → NetEase → LRCLIB → Musixmatch) ────
      const backendResult = await searchViaBackend(query, title, artist);

      if (abort.signal.aborted) return;

      if (backendResult && backendResult.status !== "not_found" && backendResult.status !== "error") {
        const score = backendResult.confidence;

        // Backend already scored — use its confidence directly
        if (score < CONFIDENCE_THRESHOLD) {
          // Low confidence even from backend → ask talent to confirm
          setResult({
            status:     "confirm",
            lrc:        backendResult.lrc,
            title:      backendResult.title,
            artist:     backendResult.artist,
            duration:   backendResult.duration,
            query,
            confidence: score,
          });
        } else {
          setResult({
            status:     backendResult.status,
            lrc:        backendResult.lrc,
            title:      backendResult.title,
            artist:     backendResult.artist,
            duration:   backendResult.duration,
            query,
            confidence: score,
          });
        }
        return;
      }

      // ── Backend not available or returned not_found ────────────────────
      // Fall through to LRCLIB direct (development fallback)

      // Pass 1: flat query
      let tracks   = await flatSearch(query);
      let best     = pickBestTrack(tracks, query);
      let topScore = best?.score ?? 0;

      // Pass 2: structured title + artist search
      if (!best || topScore < CONFIDENCE_THRESHOLD) {
        if (artist) {
          const tracks2 = await structuredSearch(title, artist);
          if (!abort.signal.aborted) {
            const best2 = pickBestTrack(tracks2, query);
            if (best2 && best2.score > topScore) {
              best      = best2;
              topScore  = best2.score;
            }
          }
        }
      }

      if (abort.signal.aborted) return;

      if (!best) {
        setResult({ status: "not_found", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });
        return;
      }

      const { track, score } = best;
      const hasSynced = !!(track.syncedLyrics && track.syncedLyrics.trim().length > 20);
      const lrc       = trackToLrc(track);

      if (score < CONFIDENCE_THRESHOLD) {
        setResult({
          status: "confirm", lrc,
          title: track.name, artist: track.artistName,
          duration: track.duration, query, confidence: score,
        });
        return;
      }

      setResult({
        status:     hasSynced ? "found" : "plain",
        lrc,
        title:      track.name,
        artist:     track.artistName,
        duration:   track.duration,
        query,
        confidence: score,
      });

    } catch (err: any) {
      if (abort.signal.aborted) return;
      console.warn("[useLyricsAutoFetch]", err);
      setResult({ status: "error", lrc: "", title: "", artist: "", duration: 0, query, confidence: 0 });
    }
  }, []);

  return { result, fetchForFilename, fetchForTitleArtist, confirmResult, reset };
}